import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';

export type SiteMasterSite = {
  id: number;
  site_code: string;
  site_name: string;
  enabled: boolean;
};

export type SiteMasterPromotionArea = { id: number; name: string; enabled: boolean };

type SessionResp = {
  ok: boolean;
  err?: string;
  master?: { id: number; phone: string; display_name: string };
  sites?: SiteMasterSite[];
  promotion_areas?: SiteMasterPromotionArea[];
  current_site?: SiteMasterSite | null;
  current_site_id?: number;
  list_all_sites?: boolean;
  mp_analytics_enabled?: boolean;
  has_mp_analytics?: boolean;
};

export function useSiteMasterSession() {
  const [loading, setLoading] = useState(true);
  const [ok, setOk] = useState(false);
  const [master, setMaster] = useState<SessionResp['master']>(null);
  const [sites, setSites] = useState<SiteMasterSite[]>([]);
  const [promotionAreas, setPromotionAreas] = useState<SiteMasterPromotionArea[]>([]);
  const [currentSite, setCurrentSite] = useState<SiteMasterSite | null>(null);
  const [currentSiteId, setCurrentSiteId] = useState(0);
  const [mpAnalyticsEnabled, setMpAnalyticsEnabled] = useState(false);
  const [hasMpAnalytics, setHasMpAnalytics] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<SessionResp>('site_master_session', { method: 'GET' });
      if (r.ok && r.master) {
        setOk(true);
        setMaster(r.master);
        setSites(r.sites ?? []);
        setPromotionAreas(r.promotion_areas ?? []);
        setCurrentSite(r.current_site ?? null);
        setCurrentSiteId(typeof r.current_site_id === 'number' ? r.current_site_id : (r.current_site?.id ?? 0));
        setMpAnalyticsEnabled(!!r.mp_analytics_enabled);
        setHasMpAnalytics(!!r.has_mp_analytics);
      } else {
        setOk(false);
        setMaster(null);
        setSites([]);
        setPromotionAreas([]);
        setCurrentSite(null);
        setCurrentSiteId(0);
        setMpAnalyticsEnabled(false);
        setHasMpAnalytics(false);
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        setOk(false);
      } else {
        setOk(false);
      }
      setMaster(null);
      setSites([]);
      setPromotionAreas([]);
      setCurrentSite(null);
      setCurrentSiteId(0);
      setMpAnalyticsEnabled(false);
      setHasMpAnalytics(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    loading,
    ok,
    master,
    sites,
    promotionAreas,
    currentSite,
    currentSiteId,
    mpAnalyticsEnabled,
    hasMpAnalytics,
    refresh,
    setCurrentSite,
  };
}
